<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  target: { type: String, default: 'message' },
  templateType: { type: String, default: 'email' },
});

const emit = defineEmits(['insert', 'update:target']);

const { t } = useI18n();

const targets = computed(() => {
  const messageTarget = {
    value: 'message',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.FORM.MESSAGE'),
  };
  if (props.templateType === 'message') return [messageTarget];

  return [
    { value: 'subject', label: t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SUBJECT') },
    messageTarget,
    { value: 'html_content', label: t('CAMPAIGN.EMAIL.TEMPLATES.FORM.HTML') },
    { value: 'signature', label: t('CAMPAIGN.EMAIL.TEMPLATES.FORM.SIGNATURE') },
  ];
});

const variables = computed(() => [
  {
    token: '{{contact.name}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.CONTACT_NAME'),
  },
  {
    token: '{{contact.first_name}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.CONTACT_FIRST_NAME'),
  },
  {
    token: '{{contact.email}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.CONTACT_EMAIL'),
  },
  {
    token: '{{agent.name}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.AGENT_NAME'),
  },
  {
    token: '{{inbox.name}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.INBOX_NAME'),
  },
  {
    token: '{{account.name}}',
    label: t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.ACCOUNT_NAME'),
  },
]);
</script>

<template>
  <section class="border-y border-n-weak py-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.EMAIL.TEMPLATES.VARIABLES.TITLE') }}
      </h4>
      <div
        class="flex max-w-full overflow-x-auto rounded-md border border-n-weak bg-n-alpha-2 p-0.5"
      >
        <button
          v-for="item in targets"
          :key="item.value"
          type="button"
          class="h-7 whitespace-nowrap rounded px-2 text-xs font-medium text-n-slate-11 hover:text-n-slate-12"
          :class="{
            'bg-n-solid-2 text-n-slate-12 shadow-sm': target === item.value,
          }"
          :aria-pressed="target === item.value"
          @click="emit('update:target', item.value)"
        >
          {{ item.label }}
        </button>
      </div>
    </div>
    <div class="mt-3 flex flex-wrap gap-2">
      <button
        v-for="variable in variables"
        :key="variable.token"
        type="button"
        data-testid="liquid-variable"
        class="group rounded-md border border-n-weak bg-n-alpha-2 px-2.5 py-2 text-left hover:border-n-brand hover:bg-n-brand/5"
        :title="variable.token"
        @click="emit('insert', variable.token)"
      >
        <span class="block text-xs font-medium text-n-slate-12">
          {{ variable.label }}
        </span>
        <code class="block text-[11px] text-n-blue-11">
          {{ variable.token }}
        </code>
      </button>
    </div>
  </section>
</template>
